import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SHOW_LANGUAGE_SWITCHER } from '../../../i18n';
import { Dropdown, Skeleton } from 'antd';

import useSmartNavigate from 'hooks/useSmartNavigate';
import { getImage } from 'helpers';
import { loadingStatus } from 'helpers/fetcher';
import SearchWithSuggest from 'components/form/SearchWithSuggest';
import Icon from 'components/Icon';

const MobileHeader = ({
	customer,
	handleSigninModalOpen,
	data,
	total,
	handleCityClick,
	handleLogoClick,
	status,
}) => {
	const { t, i18n } = useTranslation();
	const { navigate, categoryNavigate } = useSmartNavigate();
	const [menuOpen, setMenuOpen] = useState(false);
	const [catalogOpen, setCatalogOpen] = useState(false);
	const [expandedCategories, setExpandedCategories] = useState({});
	const [searchValue, setSearchValue] = useState('');
	const cartProducts = useSelector(state => state.cart.cartProducts);
	const { data: menuData, status: menuStatus } = useSelector(
		state => state.menu.categoriesList
	);

	const handleSearch = () => {
		if (searchValue.trim()) {
			navigate(`/catalog?search=${encodeURIComponent(searchValue.trim())}`);
			setSearchValue('');
		}
	};

	const handleMenuItemClick = (path, requiresAuth = false) => {
		setMenuOpen(false);
		if (requiresAuth && !customer.token) {
			handleSigninModalOpen();
		} else {
			navigate(path);
		}
	};

	const handleCategoryClick = category => {
		setCatalogOpen(false);
		categoryNavigate({ key: category.key });
	};

	const toggleCategory = categoryKey => {
		setExpandedCategories(prev => ({
			...prev,
			[categoryKey]: !prev[categoryKey],
		}));
	};

	const cartTotal =
		cartProducts.data.totals?.find(t => t.code === 'total')?.text || '';

	return (
		<>
			{/* Верхний бар с городом и телефоном */}
			<div
				className='mobile-top-bar'
				style={{
					backgroundImage: `url(${
						process.env.PUBLIC_URL || ''
					}/images/header-background.webp)`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
				}}
			>
				<div className='mobile-container'>
					<Dropdown
						trigger={['click']}
						menu={{
							items: data.cities_list,
							onClick: handleCityClick,
						}}
						overlayClassName='city-dropdown-overlay'
					>
						{status === loadingStatus.SUCCEEDED ? (
							<button className='mobile-city-button'>
								<Icon name='icon-map-marker' className='mobile-city-icon' />
								<span className='mobile-city-name'>
									{data.cities_list?.[customer.store_id]?.label || t('header.city')}
								</span>
								<Icon name='icon-arrow-down' className='mobile-city-arrow' />
							</button>
						) : (
							<Skeleton.Button active size='small' />
						)}
					</Dropdown>
					{status === loadingStatus.SUCCEEDED && data.telephone && (
						<a href={`tel:${data.telephone}`} className='mobile-phone-link'>
							<Icon name='icon-phone' className='mobile-phone-icon' />
							<span>{data.telephone}</span>
						</a>
					)}
				</div>
			</div>

			{/* Основной хедер с логотипом, каталогом и бургером */}
			<div className='mobile-header'>
				<div className='mobile-container'>
					<a href='/' onClick={handleLogoClick} className='mobile-logo'>
						<img
							src={getImage('catalog/logo_new-new-mirror.jpg')}
							alt={t('common.rayan')}
						/>
					</a>
					<div className='mobile-header-right'>
						<button
							className='mobile-catalog-button'
							data-click-key='nav:catalog'
							onClick={() => setCatalogOpen(true)}
						>
							<Icon name='icon-catalog' className='mobile-catalog-icon' />
							<span>{t('common.catalog')}</span>
						</button>
						<button
							type='button'
							className='mobile-burger-button'
							data-click-key='nav:menu'
							onClick={() => setMenuOpen(true)}
						>
							<span className='mobile-burger-button-inner'>
								<Icon
									name='icon-burger-menu'
									className='mobile-burger-icon'
									ariaLabel={t('common.menu')}
									ariaHidden={false}
								/>
								{cartProducts.data.count > 0 && (
									<span className='mobile-burger-badge'>
										{cartProducts.data.count > 99
											? '99+'
											: cartProducts.data.count}
									</span>
								)}
							</span>
						</button>
					</div>
				</div>
			</div>

			{/* Строка поиска */}
			<div className='mobile-search'>
				<div className='mobile-container'>
					<div className='mobile-search-wrapper'>
						<Icon name='icon-search' className='mobile-search-icon' />
						<input
							type='text'
							placeholder={t('common.searchPlaceholder')}
							value={searchValue}
							onChange={e => setSearchValue(e.target.value)}
							onKeyDown={e => e.key === 'Enter' && handleSearch()}
							className='mobile-search-input'
						/>
						<button className='mobile-search-button' onClick={handleSearch}>
							{t('common.find')}
						</button>
					</div>
					<SearchWithSuggest
						isDesktop={false}
						onClose={() => {}}
						hideInput={true}
						externalSearchValue={searchValue}
					/>
				</div>
			</div>

			{/* Бургер меню */}
			{menuOpen && (
				<div className='mobile-menu-overlay' onClick={() => setMenuOpen(false)}>
					<div className='mobile-menu' onClick={e => e.stopPropagation()}>
						<div className='mobile-menu-header'>
							<span className='mobile-menu-title'>{t('header.menuTitle')}</span>
							<button
								className='mobile-menu-close'
								data-click-key='nav:menu-close'
								onClick={() => setMenuOpen(false)}
							>
								<Icon name='icon-close' ariaLabel={t('common.close')} ariaHidden={false} />
							</button>
						</div>
						<div className='mobile-menu-content'>
							{/* Корзина */}
							<a
								href='/cart'
								className={`mobile-menu-item ${
									cartProducts.data.count && cartProducts.data.count > 0
										? 'mobile-menu-item-cart-active'
										: ''
								}`}
								onClick={e => {
									e.preventDefault();
									handleMenuItemClick('/cart');
								}}
							>
								<Icon
									name={
										cartProducts.data.count && cartProducts.data.count > 0
											? 'icon-cart-red'
											: 'icon-cart'
									}
								/>
								<span className='mobile-menu-cart-badge'>
									{cartTotal || '0 ₽'}
								</span>
							</a>

							{/* Кабинет */}
							<a
								href='/account'
								className='mobile-menu-item'
								onClick={e => {
									e.preventDefault();
									handleMenuItemClick('/account', true);
								}}
							>
								<Icon name='icon-user' />
								<span>{t('header.account')}</span>
							</a>

							{/* Заказы */}
							<a
								href='/account/history'
								className='mobile-menu-item'
								onClick={e => {
									e.preventDefault();
									handleMenuItemClick('/account/history', true);
								}}
							>
								<Icon name='icon-clock' />
								<span>{t('header.orders')}</span>
							</a>

							{/* Избранное */}
							<a
								href='/account/wishlist'
								className='mobile-menu-item'
								onClick={e => {
									e.preventDefault();
									handleMenuItemClick('/account/wishlist', true);
								}}
							>
								<Icon name='icon-heart-black' />
								<span>{t('header.favorites')}</span>
							</a>

							{/* Вакансии */}
							<a
								href='/page/9'
								className='mobile-menu-item mobile-menu-item-text'
								onClick={e => {
									e.preventDefault();
									handleMenuItemClick('/page/9');
								}}
							>
								<span>{t('header.vacancies')}</span>
							</a>

							{/* Оставьте отзыв */}
							<a
								href='/contact'
								className='mobile-menu-item mobile-menu-item-text'
								onClick={e => {
									e.preventDefault();
									handleMenuItemClick('/contact');
								}}
							>
								<span>{t('header.leaveReview')}</span>
							</a>

							{/* Новости */}
							<a
								href='/news'
								className='mobile-menu-item mobile-menu-item-text'
								onClick={e => {
									e.preventDefault();
									handleMenuItemClick('/news');
								}}
							>
								<span>{t('header.news')}</span>
							</a>

							{/* Сертификаты */}
							{data.top_menu?.certificate && (
								<a
									href={`/page/${data.top_menu.certificate.id}`}
									className='mobile-menu-item mobile-menu-item-text'
									onClick={e => {
										e.preventDefault();
										handleMenuItemClick(
											`/page/${data.top_menu.certificate.id}`
										);
									}}
								>
									<span>{t('header.certificates')}</span>
								</a>
							)}

							{/* Акции (временно скрыто)
							<a
								href='/page/promotions'
								className='mobile-menu-item mobile-menu-item-promo'
								onClick={e => {
									e.preventDefault();
									handleMenuItemClick('/page/promotions');
								}}
							>
								<Icon name='icon-percent' />
								<span>Акции</span>
							</a>
							*/}

							{/* О компании */}
							{data.top_menu?.about && (
								<a
									href={`/page/${data.top_menu.about.id}`}
									className='mobile-menu-item'
									onClick={e => {
										e.preventDefault();
										handleMenuItemClick(`/page/${data.top_menu.about.id}`);
									}}
								>
									<Icon name='icon-info' />
									<span>{t('header.aboutCompany')}</span>
								</a>
							)}

							{/* Доставка и оплата */}
							{data.top_menu?.delivery && (
								<a
									href={`/page/${data.top_menu.delivery.id}`}
									className='mobile-menu-item'
									onClick={e => {
										e.preventDefault();
										handleMenuItemClick(`/page/${data.top_menu.delivery.id}`);
									}}
								>
									<Icon name='icon-shipping' />
									<span>{t('header.deliveryAndPayment')}</span>
								</a>
							)}

							<a
								href='https://is.rayanhalal.ru/'
								className='mobile-menu-item'
								target='_blank'
								rel='noopener noreferrer'
							>
								<svg viewBox='0 0 20 20' aria-hidden='true'>
									<path d='M3 6.2L10 3.2L17 6.2V13.8L10 16.8L3 13.8V6.2Z' fill='none' stroke='#1C1C1C' strokeWidth='1.6' strokeLinejoin='round' />
									<path d='M3 6.2L10 9.2L17 6.2' fill='none' stroke='#1C1C1C' strokeWidth='1.6' strokeLinejoin='round' />
									<path d='M10 9.2V16.8' fill='none' stroke='#1C1C1C' strokeWidth='1.6' strokeLinejoin='round' />
								</svg>
								<span>{t('header.wholesale')}</span>
							</a>

							{/* Контакты */}
							<a
								href='/contact'
								className='mobile-menu-item'
								onClick={e => {
									e.preventDefault();
									handleMenuItemClick('/contact');
								}}
							>
								<Icon name='icon-envelope' />
								<span>{t('header.contacts')}</span>
							</a>

							{SHOW_LANGUAGE_SWITCHER && (
								<button
									type='button'
									className='mobile-menu-item lang-switch-button'
									onClick={() => {
										i18n.changeLanguage(i18n.language === 'ru' ? 'en' : 'ru');
										setMenuOpen(false);
									}}
								>
									<span>
										{i18n.language === 'ru' ? 'English' : 'Русский'}
									</span>
								</button>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Меню каталога */}
			{catalogOpen && (
				<div
					className='mobile-menu-overlay'
					onClick={() => setCatalogOpen(false)}
				>
					<div
						className='mobile-menu mobile-catalog-menu'
						onClick={e => e.stopPropagation()}
					>
						<div className='mobile-menu-header'>
							<span className='mobile-menu-title'>{t('header.catalogTitle')}</span>
							<button
								className='mobile-menu-close'
								data-click-key='nav:catalog-close'
								onClick={() => setCatalogOpen(false)}
							>
								<Icon name='icon-close' ariaLabel={t('common.close')} ariaHidden={false} />
							</button>
						</div>
						<div className='mobile-menu-content mobile-catalog-content'>
							{menuStatus === loadingStatus.SUCCEEDED && menuData.categories ? (
								menuData.categories.map(category => (
									<div key={category.key} className='mobile-catalog-category'>
										<div
											className='mobile-catalog-item'
											onClick={() => {
												if (category.children && category.children.length > 0) {
													toggleCategory(category.key);
												} else {
													handleCategoryClick(category);
												}
											}}
										>
											<span
												className={
													expandedCategories[category.key]
														? 'mobile-catalog-item-active'
														: ''
												}
											>
												{category.label}
											</span>
											{category.children && category.children.length > 0 && (
												<Icon
													name='icon-arrow-down-small'
													className={`mobile-catalog-arrow ${
														expandedCategories[category.key]
															? 'mobile-catalog-arrow-open'
															: ''
													}`}
												/>
											)}
										</div>
										{expandedCategories[category.key] && category.children && (
											<div className='mobile-catalog-subcategories'>
												{category.children.map(subcategory => (
													<div
														key={subcategory.key}
														className='mobile-catalog-subcategory'
														onClick={() => handleCategoryClick(subcategory)}
													>
														{subcategory.label}
													</div>
												))}
											</div>
										)}
									</div>
								))
							) : (
								<div className='mobile-catalog-loading'>
									<Skeleton active paragraph={{ rows: 8 }} />
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default MobileHeader;
